import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from 'src/lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogClose = DialogPrimitive.Close;

const DialogPortal = ({ children }) => <DialogPrimitive.Portal>{children}</DialogPrimitive.Portal>;

const DialogOverlay = React.forwardRef(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
});

const DialogContent = React.forwardRef(function DialogContent({ className, children, onClose, ...props }, ref) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 border bg-card rounded-2xl shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out overflow-hidden',
          className,
        )}
        {...props}>
        {children}
        {onClose && (
          <DialogClose
            onClick={onClose}
            className='absolute right-4 top-4 rounded-lg bg-background w-[30px] h-[30px] flex items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring'>
            <X size={16} strokeWidth={1.5} />
            <span className='sr-only'>Close</span>
          </DialogClose>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

const DialogHeader = ({ className, ...props }) => (
  <div className={cn('px-7 pt-6 pb-5 border-b border-border', className)} {...props} />
);

const DialogFooter = ({ className, ...props }) => (
  <div className={cn('flex justify-end gap-2.5 px-7 py-4', className)} {...props} />
);

const DialogTitle = React.forwardRef(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn('text-lg font-display leading-none tracking-tight', className)}
      {...props}
    />
  );
});

const DialogDescription = React.forwardRef(function DialogDescription({ className, ...props }, ref) {
  return <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />;
});

const DialogBody = ({ className, ...props }) => <div className={cn('px-7 py-5', className)} {...props} />;

export {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogBody,
};
